import { useEffect, useState } from "react";
import api, { extractErr } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Ticket, Trash2, Plus } from "lucide-react";

const rupee = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;
const BLANK = { code: "", description: "", type: "percent", value: "", max_discount: "", applies_to: "all", usage_limit_total: "", expires_at: "", is_active: true };

const PLAN_LABEL = { all: "All plans", website: "Website only", website_app: "Website + App only", website_app_ios: "iOS + Android + Website only" };

export default function SuperCoupons() {
  const [coupons, setCoupons] = useState([]);
  const [form, setForm] = useState(BLANK);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/platform/coupons");
      setCoupons(data);
    } catch (e) { toast.error(extractErr(e)); }
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        code: form.code,
        description: form.description,
        type: form.type,
        value: Number(form.value),
        max_discount: Number(form.max_discount || 0),
        applies_to: form.applies_to,
        usage_limit_total: Number(form.usage_limit_total || 0),
        expires_at: form.expires_at ? new Date(form.expires_at).toISOString() : null,
        is_active: true,
      };
      await api.post("/platform/coupons", payload);
      toast.success("Coupon created");
      setForm(BLANK);
      load();
    } catch (e) { toast.error(extractErr(e)); }
    finally { setSaving(false); }
  };

  const toggleActive = async (c) => {
    try {
      await api.put(`/platform/coupons/${c.id}`, {
        code: c.code, description: c.description, type: c.type, value: c.value,
        max_discount: c.max_discount, applies_to: c.applies_to,
        usage_limit_total: c.usage_limit_total,
        expires_at: c.expires_at || null, is_active: !c.is_active,
      });
      load();
    } catch (e) { toast.error(extractErr(e)); }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete coupon ${c.code}?`)) return;
    try { await api.delete(`/platform/coupons/${c.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(extractErr(e)); }
  };

  return (
    <div className="space-y-6 max-w-4xl">
      <div>
        <h1 className="font-display text-3xl font-bold">Signup coupons</h1>
        <p className="text-slate-500 text-sm mt-1">Discount codes merchants can apply at store-creation checkout. Applied to the plan price.</p>
      </div>

      {/* Create form */}
      <Card className="bg-slate-900 border-slate-800 p-5">
        <form onSubmit={create} className="space-y-4">
          <div className="flex items-center gap-2 text-indigo-300"><Plus className="w-4 h-4" /><span className="text-sm font-semibold">New coupon</span></div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-slate-100">Code</Label>
              <Input required data-testid="coupon-code" placeholder="LAUNCH50" className="bg-slate-800 border-slate-700 text-white uppercase mt-1" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} />
            </div>
            <div>
              <Label className="text-slate-100">Applies to</Label>
              <select data-testid="coupon-applies" className="w-full mt-1 h-10 rounded-md bg-slate-800 border border-slate-700 text-white px-3 text-sm" value={form.applies_to} onChange={(e) => setForm({ ...form, applies_to: e.target.value })}>
                <option value="all">All plans</option>
                <option value="website">Website only</option>
                <option value="website_app">Website + App only</option>
                <option value="website_app_ios">iOS + Android + Website only</option>
              </select>
            </div>
            <div>
              <Label className="text-slate-100">Discount type</Label>
              <select data-testid="coupon-type" className="w-full mt-1 h-10 rounded-md bg-slate-800 border border-slate-700 text-white px-3 text-sm" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}>
                <option value="percent">Percentage (%)</option>
                <option value="flat">Flat (₹)</option>
              </select>
            </div>
            <div>
              <Label className="text-slate-100">{form.type === "percent" ? "Percent off (1–100)" : "Amount off (₹)"}</Label>
              <Input required type="number" min="0" step="0.01" data-testid="coupon-value" className="bg-slate-800 border-slate-700 text-white mt-1" value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} />
            </div>
            {form.type === "percent" && (
              <div>
                <Label className="text-slate-100">Max discount cap (₹, optional)</Label>
                <Input type="number" min="0" step="0.01" data-testid="coupon-cap" placeholder="0 = no cap" className="bg-slate-800 border-slate-700 text-white mt-1" value={form.max_discount} onChange={(e) => setForm({ ...form, max_discount: e.target.value })} />
              </div>
            )}
            <div>
              <Label className="text-slate-100">Usage limit (optional)</Label>
              <Input type="number" min="0" data-testid="coupon-limit" placeholder="0 = unlimited" className="bg-slate-800 border-slate-700 text-white mt-1" value={form.usage_limit_total} onChange={(e) => setForm({ ...form, usage_limit_total: e.target.value })} />
            </div>
            <div>
              <Label className="text-slate-100">Expires on (optional)</Label>
              <Input type="date" data-testid="coupon-expiry" className="bg-slate-800 border-slate-700 text-white mt-1" value={form.expires_at} onChange={(e) => setForm({ ...form, expires_at: e.target.value })} />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-slate-100">Description (optional)</Label>
              <Input data-testid="coupon-desc" placeholder="Launch offer" className="bg-slate-800 border-slate-700 text-white mt-1" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </div>
          </div>
          <Button type="submit" disabled={saving} data-testid="coupon-create" className="bg-indigo-500 hover:bg-indigo-600">{saving ? "Creating…" : "Create coupon"}</Button>
        </form>
      </Card>

      {/* List */}
      <div className="space-y-3">
        {coupons.length === 0 && <p className="text-slate-500 text-sm">No coupons yet.</p>}
        {coupons.map((c) => (
          <Card key={c.id} data-testid={`coupon-row-${c.code}`} className="bg-slate-900 border-slate-800 p-4 flex items-center gap-4">
            <div className="w-9 h-9 rounded-lg bg-indigo-500/20 border border-indigo-400/40 flex items-center justify-center flex-shrink-0"><Ticket className="w-4 h-4 text-indigo-300" /></div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-mono-nums font-bold text-white">{c.code}</span>
                <span className="text-xs px-2 py-0.5 rounded-full bg-slate-800 text-slate-300 border border-slate-700">{c.type === "percent" ? `${c.value}% off` : `${rupee(c.value)} off`}</span>
                <span className="text-xs px-2 py-0.5 rounded-full bg-slate-800 text-slate-400 border border-slate-700">{PLAN_LABEL[c.applies_to] || c.applies_to}</span>
                {!c.is_active && <span className="text-xs px-2 py-0.5 rounded-full bg-red-500/15 text-red-300 border border-red-500/30">Inactive</span>}
              </div>
              <div className="text-xs text-slate-500 mt-1">
                {c.description ? `${c.description} · ` : ""}
                Used {c.times_used || 0}{c.usage_limit_total ? ` / ${c.usage_limit_total}` : ""}
                {c.max_discount ? ` · cap ${rupee(c.max_discount)}` : ""}
                {c.expires_at ? ` · expires ${new Date(c.expires_at).toLocaleDateString()}` : ""}
              </div>
            </div>
            <Button type="button" variant="outline" onClick={() => toggleActive(c)} data-testid={`coupon-toggle-${c.code}`} className="border-slate-600 text-slate-200 hover:bg-slate-800">{c.is_active ? "Disable" : "Enable"}</Button>
            <Button type="button" variant="ghost" onClick={() => remove(c)} data-testid={`coupon-delete-${c.code}`} className="text-red-300 hover:text-red-200 hover:bg-red-500/10"><Trash2 className="w-4 h-4" /></Button>
          </Card>
        ))}
      </div>
    </div>
  );
}
